import * as React from "react";
import {database, globalSettings} from "./config";
import {divideMaterials, inventoryFromMaterialsMap, numberWithCommas, toTitleCase} from "./util";
import {STATUS_QUEUED} from "./model/data";
import {GlobalInfo} from "./context";
import {Card, Dropdown, DropdownButton} from "react-bootstrap";
import {EngineerOnly, MaterialList, Toggle, ShipComponentsTable, CAPITAL_COMPONENTS, CapitalComponentsTable} from "./Common";
import {postToDiscord} from "./discord";
import {toast} from "react-toastify";
import {SHIP_DATA} from "./model/fixtures";
import {withTranslation} from "react-i18next";

class RecipesPage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      location: globalSettings.defaultLocationShipOrders,
      quantity: 1,
      shipType: "doctrine",
    }
  }

  setLocation(e) {
    this.setState({
      location: e.target.value
    });
  }

  // calculateCostBasis = (recipe) => {
  //   let price = recipe.isk * globalSettings.purchasePriceFixedCostRatio;
  //   price += recipe.blueprint_price * globalSettings.purchasePriceBlueprintCostRatio;
  //   Object.keys(recipe.materials).forEach(name => {
  //     let material = this.context.materialsMap[name];
  //     let avgCost = material.price * 0.85 * (1+globalSettings.engineerPaymentMaterialCostRatio);
  //     // if(material.costBasis && material.totalInventory) {
  //     //   avgCost = (material.costBasis||0)  / (material.totalInventory||0);
  //     // }
  //     price += avgCost * recipe.materials[name] * globalSettings.shipTypeToMaterialEfficiency[recipe.shipType || recipe.ship_type];
  //   });
  //   return price;
  // };

  calculateMaterials = (recipe, isComponent = false) => {
    if (!recipe) {
      return {};
    }

    let finalMaterials = {};
    // Get efficiency from globalSettings based on ship type
    const efficiencyMap = this.context.globalSettings?.shipTypeToMaterialEfficiency || {};
    const shipType = recipe.ship_type;
    // For components, use 'component' efficiency (0.84), otherwise look up by ship type
    const efficiency = (shipType === 'Components' || shipType === 'module')
      ? (efficiencyMap['component'] || 0.84)
      : (efficiencyMap[shipType] || efficiencyMap['capital'] || 0.86);
    const quantity = isComponent ? (recipe.quantity || 1) : this.state.quantity;

    // Calculate materials from direct materials
    Object.keys(recipe.materials || {}).forEach(name => {
      // Check if this material is actually a component by looking for its recipe
      const componentRecipe = this.context.recipes.find(r => r.name === name);
      if (componentRecipe && componentRecipe.ship_type === 'Components') {
        // If it's a component, calculate its materials recursively
        // Apply ship's efficiency to component count first (same logic as calculateComponents)
        const shipType = recipe.ship_type;
        const shipEfficiency = efficiencyMap[shipType] || efficiencyMap['capital'] || 0.82;
        const adjustedComponentQty = Math.ceil(recipe.materials[name] * quantity * shipEfficiency);

        // Calculate component materials with per-material rounding:
        // For each raw material: CEIL(rawAmount * 0.84) * componentQty
        // This matches the sheet formula: ROUNDUP(material * efficiency) * count
        const componentEfficiency = efficiencyMap['component'] || 0.84;
        const componentMaterials = {};

        if (componentRecipe.materials) {
          Object.entries(componentRecipe.materials).forEach(([matName, rawAmount]) => {
            // Round UP per-material after applying efficiency, THEN multiply by component count
            const perComponentAmount = Math.ceil(rawAmount * componentEfficiency);
            componentMaterials[matName] = perComponentAmount * adjustedComponentQty;
          });
        }

        // Merge component materials into final materials
        Object.keys(componentMaterials).forEach(matName => {
          finalMaterials[matName] = (finalMaterials[matName] || 0) + componentMaterials[matName];
        });
      } else {
        // Regular material
        finalMaterials[name] = (recipe.materials[name] || 0) * efficiency * quantity;
      }
    });

    // Calculate materials from components field if it exists
    if (recipe.components) {
      Object.keys(recipe.components).forEach(componentName => {
        const componentRecipe = this.context.recipes.find(r => r.name === componentName);
        if (componentRecipe) {
          // Apply ship's efficiency to component count first (same logic as calculateComponents)
          const shipType = recipe.ship_type;
          const shipEfficiency = efficiencyMap[shipType] || efficiencyMap['capital'] || 0.82;
          const componentQuantity = Math.ceil((recipe.components[componentName] || 0) * quantity * shipEfficiency);

          // Calculate component materials with per-material rounding:
          // For each raw material: CEIL(rawAmount * 0.84) * componentQty
          const componentEfficiency = efficiencyMap['component'] || 0.84;
          const componentMaterials = {};

          if (componentRecipe.materials) {
            Object.entries(componentRecipe.materials).forEach(([matName, rawAmount]) => {
              // Round UP per-material after applying efficiency, THEN multiply by component count
              const perComponentAmount = Math.ceil(rawAmount * componentEfficiency);
              componentMaterials[matName] = perComponentAmount * componentQuantity;
            });
          }

          // Merge component materials into final materials
          Object.keys(componentMaterials).forEach(name => {
            finalMaterials[name] = (finalMaterials[name] || 0) + componentMaterials[name];
          });
        }
      });
    }
    
    return finalMaterials;
  };

  calculateComponents = (recipe) => {
    if (!recipe) {
      return {};
    }

    let finalComponents = {};
    const quantity = this.state.quantity;
    // Apply ship_type-specific efficiency to component counts and round UP (DB has raw 100% values)
    // e.g., Dreadnoughts use 82%, Supercarrier uses 77%, Tactical Carrier uses 90%
    const shipType = recipe.ship_type;
    const efficiencyMap = this.context.globalSettings?.shipTypeToMaterialEfficiency || {};
    const efficiency = efficiencyMap[shipType] || efficiencyMap['capital'] || 0.82;

    // Check materials for capital components using our reference map
    Object.keys(recipe.materials || {}).forEach(name => {
      if (CAPITAL_COMPONENTS[name]) {
        const rawQty = (recipe.materials[name] || 0) * quantity;
        finalComponents[name] = Math.ceil(rawQty * efficiency);
      }
    });

    // Also check explicit components field if it exists (as backup)
    if (recipe.components) {
      Object.keys(recipe.components).forEach(componentName => {
        if (CAPITAL_COMPONENTS[componentName]) {
          const rawQty = (recipe.components[componentName] || 0) * quantity;
          finalComponents[componentName] = Math.ceil(rawQty * efficiency);
        }
      });
    }

    return finalComponents;
  };

  // Calculate price from pre-calculated materials
  calculatePriceFromMaterials = (recipe, materials, isComponent = false) => {
    if (!recipe || !materials) {
      return NaN;
    }

    let materialsCost = 0;
    let hasMissingMaterials = false;
    
    Object.entries(materials).forEach(([name, quantity]) => {
      // Check if this material is actually a component
      const componentRecipe = this.context.recipes.find(r => r.name === name);
      if (componentRecipe && componentRecipe.ship_type === 'Components') {
        // For components, calculate price recursively
        const componentMaterials = this.calculateMaterials(componentRecipe, true);
        const componentPrice = this.calculatePriceFromMaterials(componentRecipe, componentMaterials, true);
        materialsCost += componentPrice * quantity;
      } else {
        // Regular material
        const material = this.context.materialsMap[name];
        if (!material) {
          hasMissingMaterials = true;
          return;
        }

        // Calculate material cost based on type
        let materialCost;
        if (material.type === 'mineral' || material.type === 'planetary') {
          materialCost = (material.price || 0) * quantity * globalSettings.purchasePriceMaterialCostRatio;
        } else if (material.type === 'ore') {
          return; // Skip ores
        } else if (material.type === 'ship') {
          // For ships used as materials
          const shipRecipe = this.context.recipes.find(r => r.name === name);
          if (shipRecipe) {
            const shipMaterials = this.calculateMaterials(shipRecipe, true);
            const shipCost = this.calculatePriceFromMaterials(shipRecipe, shipMaterials, true);
            materialCost = shipCost * quantity;
          } else {
            materialCost = (material.price || 0) * quantity * globalSettings.purchasePriceMaterialCostRatio;
          }
        } else {
          materialCost = (material.price || 0) * quantity * globalSettings.purchasePriceMaterialCostRatio;
        }

        materialsCost += materialCost;
      }
    });

    // Add base costs
    let baseIskCost = (recipe.raw_manufacture_cost || recipe.isk || 0) * globalSettings.purchasePriceFixedCostRatio * (isComponent ? 1 : this.state.quantity);
    let blueprintCost = (recipe.blueprint_price || 0) * (isComponent ? 1 : this.state.quantity);
    
    let price = materialsCost + baseIskCost + blueprintCost;

    if (hasMissingMaterials) {
      price = { value: price, incomplete: true };
    }

    return price;
  };

  // Check if a recipe is a capital ship (uses component-based pricing)
  isCapitalShip = (recipe) => {
    const capitalTypes = ['Carriers', 'Dreadnoughts', 'Force Auxiliaries', 'Freighters',
                          'Jump Freighters', 'Capital Industrial Ship', 'Lancer',
                          'Tactical Carrier', 'Supercarrier', 'Versatile Assault'];
    return capitalTypes.includes(recipe?.ship_type);
  };

  calculatePrice = (recipe, isComponent = false) => {
    if (!recipe) {
      return NaN;
    }

    // For capital ships, use component-based pricing
    if (this.isCapitalShip(recipe)) {
      const components = this.calculateComponents(recipe);
      let totalCost = 0;
      let totalProductionCost = 0;
      let totalMaterialCost = 0;

      // Get production cost discount (20% from skills)
      const prodCostDiscount = this.context.globalSettings?.componentProductionCostDiscount || 0.20;
      const prodCostMultiplier = 1 - prodCostDiscount;  // 0.80

      // Calculate cost for capital components
      // Apply 84% efficiency for component materials (DB has raw pre-efficiency values)
      // Apply 10% margin (purchasePriceMaterialCostRatio) to component material costs
      const capitalEfficiency = this.context.globalSettings?.shipTypeToMaterialEfficiency['component'] || 0.84;

      Object.entries(components).forEach(([name, quantity]) => {
        const componentRecipe = this.context.recipes.find(r => r.name === name);
        if (!componentRecipe) return;

        // Apply 20% production cost discount to component production costs
        const rawManufactureCost = componentRecipe.raw_manufacture_cost || 0;
        const blueprintCost = componentRecipe.blueprint_price || componentRecipe.isk || 0;
        const productionCost = (rawManufactureCost * prodCostMultiplier + blueprintCost) * quantity;

        let materialCost = 0;
        if (componentRecipe.materials) {
          Object.entries(componentRecipe.materials).forEach(([matName, amount]) => {
            const material = this.context.materialsMap[matName];
            if (material && material.price) {
              // Apply 10% margin on material costs (buyback price * 1.1)
              materialCost += material.price * amount * capitalEfficiency * globalSettings.purchasePriceMaterialCostRatio;
            }
          });
          materialCost *= quantity;
        }

        totalProductionCost += productionCost;
        totalMaterialCost += materialCost;
        totalCost += productionCost + materialCost;
      });

      // Also calculate cost for non-component materials (like ship materials)
      Object.keys(recipe.materials || {}).forEach(name => {
        // Skip if this is already handled as a capital component
        if (CAPITAL_COMPONENTS[name]) {
          return;
        }

        const quantity = (recipe.materials[name] || 0) * this.state.quantity;
        const material = this.context.materialsMap[name];
        
        if (!material) {
          return;
        }

        // Calculate material cost based on type
        let materialCost = 0;
        if (material.type === 'mineral' || material.type === 'planetary') {
          const efficiency = this.context.globalSettings?.shipTypeToMaterialEfficiency['capital'] || 0.84;
          materialCost = (material.price || 0) * quantity * efficiency * globalSettings.purchasePriceMaterialCostRatio;
        } else if (material.type === 'ore') {
          return; // Skip ores
        } else if (material.type === 'ship') {
          // For ships used as materials
          const shipRecipe = this.context.recipes.find(r => r.name === name);
          if (shipRecipe) {
            const shipMaterials = this.calculateMaterials(shipRecipe, true);
            const shipCost = this.calculatePriceFromMaterials(shipRecipe, shipMaterials, true);
            materialCost = shipCost * quantity;
          } else {
            materialCost = (material.price || 0) * quantity * globalSettings.purchasePriceMaterialCostRatio;
          }
        } else {
          materialCost = (material.price || 0) * quantity * globalSettings.purchasePriceMaterialCostRatio;
        }

        totalCost += materialCost;
      });

      const shipBaseCost = (recipe.raw_manufacture_cost || recipe.isk || 0);
      const shipBlueprintCost = (recipe.blueprint_price || 0);
      totalCost += shipBaseCost + shipBlueprintCost;

      return totalCost;
    }

    // For non-capital ships, use materials-based calculation
    const materials = this.calculateMaterials(recipe, isComponent);
    return this.calculatePriceFromMaterials(recipe, materials, isComponent);
  };

  calculateInventionPrice = (recipe) => {
    let price = recipe.blueprintIsk;
    let materials = recipe.blueprintMaterials;
    if(!price || !materials) {
      return NaN
    }
    Object.keys(materials).forEach(name => {
      let material = this.context.materialsMap[name];
      if(!material) {
        price = NaN;
        return;
      }
      price += (material.price || 0) * materials[name];
    });
    return price;
  };

  queue = (recipe, queue, pilot) => {
    const { t } = this.props;
    if (this.state.location === "") {
      toast.error(t('pages:recipes.errors.selectLocation'));
      return;
    }

    let price = this.calculatePrice(recipe);
    let materials = this.calculateMaterials(recipe);
    let notes = "";
    if (queue === "main") {
      notes = prompt(t('pages:recipes.queuePrompt', { name: recipe.name, price: numberWithCommas(price) }))
    }
    if (notes === null) {
      toast.error(t('common:messages.canceled'));
      return;
    }

    if(recipe.preorder) {
      queue = "special";
    }

    if (queue === "main" && !pilot) {
      toast.error(t('common:messages.canceled'));
      return
    }

    let name = recipe.name;
    if (this.state.quantity > 1) {
      name += ` x${this.state.quantity}`
    }

    database.collection("queue").doc().set({
      actorInfo: this.context.userData,
      actorUserInfo: this.context.userData,
      name: name,
      isk: recipe.isk * this.state.quantity,
      blueprintPrice: recipe.blueprint_price * this.state.quantity,
      price: price,
      materials: materials,
      timestamp: Date.now(),
      queue: queue,
      pilot: pilot,
      status: STATUS_QUEUED,
      notes: notes,
      location: this.state.location,
    }).then(() => {
      toast.success(t('pages:recipes.messages.addedToQueue'));
      if (queue === "main") {
        postToDiscord({
          channel: 'QUEUE',
          username: "Pandemic Industry",
          content: `${pilot} added a ${name} to the production queue in ${this.state.location}. Notes: ${notes}`
        });
      }
    });
  };

  suggest = () => {
    const { t } = this.props;
    if (!this.state.ship) {
      toast.error(t('pages:recipes.errors.selectShip'));
      return
    }
    database.collection("suggestions").doc(this.state.ship).set({
      [`users.${this.context.userData.id}`]: this.context.userData.name,
    }, {merge: true}).then(() => {
      toast.success(t('pages:recipes.messages.suggestionSent', { ship: this.state.ship }))
    })
  };

  renderRecipesTable() {
    const { t } = this.props;
    // Helper function to normalize ship types for comparison
    const normalizeShipType = (type) => {
      if (!type) return '';
      // Convert to lowercase and remove trailing 's'
      type = type.toLowerCase().replace(/s$/, '');
      // Remove 'ship' if present
      type = type.replace(/\s*ships?\s*$/, '');
      return type;
    };

    return <table className={"table"}>
      <thead>
      <tr>
        <td colSpan={4} className={"text-muted"}>
          {t('pages:recipes.discordHelp')}
        </td>
      </tr>
      <tr>
        <th>{t('common:labels.name')}</th>
        <th>{t('pages:recipes.tableHeaders.corpPrice')}</th>
        {/*<th>BPs</th>*/}
        <th>{t('common:labels.materials')}</th>
        <th>{t('common:labels.actions')}</th>
      </tr>
      </thead>
      <tbody>
      {this.context.recipes.filter(recipe => recipe.show !== false).map((recipe) => {
        if (this.state.shipType === "doctrine") {
          if (!recipe.doctrine) {
            return null;
          }
        } else if (this.state.shipType === "capital") {
          // Show the recipe if its ship_type is one of the capital ship types
          const capitalTypes = ['Carriers', 'Dreadnoughts', 'Force Auxiliaries'];
          if (!capitalTypes.includes(recipe.ship_type)) {
            return null;
          }
        } else {
          // Normalize both the recipe's ship_type and the selected shipType for comparison
          const normalizedRecipeType = normalizeShipType(recipe.ship_type || '');
          const normalizedSelectedType = normalizeShipType(this.state.shipType);
          if (normalizedRecipeType !== normalizedSelectedType) {
            return null;
          }
        }

        let inventory = inventoryFromMaterialsMap(this.context.materialsMap, this.state.location);
        let materials = this.calculateMaterials(recipe);
        let price = (recipe.ship_type === 'Carriers' || recipe.ship_type === 'Dreadnoughts' || recipe.ship_type === 'Force Auxiliaries') 
          ? this.calculatePrice(recipe) 
          : this.calculatePriceFromMaterials(recipe, materials);

        return <tr key={recipe.id}>
          <td>
            {recipe.name}
            {recipe.preorder ? " (preorder)" : null}
            {recipe.outputNum > 1 ? ` (${recipe.outputNum})` : null}
          </td>
          <td>
            {isNaN(price) ? "Unknown" : 
              typeof price === 'object' ? 
                <>{numberWithCommas(price.value)} ISK <span style={{color: 'orange'}}>(incomplete)</span></> : 
                `${numberWithCommas(price)} ISK`
            }
            {/* setting the BP price to 0 in firebase will have the app show "plus blueprint" on the app */}
            {!recipe.blueprint_price ? <div><b>plus blueprint</b></div> : null} 
            {recipe.outputNum > 1 ?
                <div>{numberWithCommas(typeof price === 'object' ? price.value/recipe.outputNum : price/recipe.outputNum)} ISK each</div> : null}
            <EngineerOnly><Toggle>
              <ul>
                <li>Corp + contract tax (+8%): {numberWithCommas(price / 0.92)} ISK</li>
                <li>At-cost price (0%): {numberWithCommas(price / globalSettings.purchasePriceMaterialCostRatio)} ISK</li>
                <li>At-cost + contract tax (+8%): {numberWithCommas(price / globalSettings.purchasePriceMaterialCostRatio / 0.92)} ISK</li>
                <li>At-cost + market tax (+18%): {numberWithCommas(price / globalSettings.purchasePriceMaterialCostRatio / 0.82)} ISK</li>
                <li>Actual market price: {numberWithCommas(recipe.market_price)} ISK</li>
                <li>Invention cost (per attempt): {numberWithCommas(this.calculateInventionPrice(recipe))}</li>
              </ul>
            </Toggle></EngineerOnly>
          </td>
          {/*<td>{numberWithCommas(recipe.market_price)} ISK</td>*/}
          {/*<td>{this.state.edit ? null : (recipe.blueprint_inventory || 0)}</td>*/}
          <td>
            <Toggle>
              {recipe.ship_type === 'Carriers' || recipe.ship_type === 'Dreadnoughts' || recipe.ship_type === 'Force Auxiliaries' ? (
                <>
                  <CapitalComponentsTable
                    recipe={recipe}
                    components={this.calculateComponents(recipe)}
                    location={this.state.location}
                  />
                  <div className="mt-3">
                    <strong>Raw Materials:</strong>
                    <MaterialList
                      materials={materials}
                      recipe={recipe}
                      isk={recipe.raw_manufacture_cost || recipe.isk}
                      blueprintIsk={recipe.blueprint_price}
                    />
                  </div>
                </>
              ) : (
                <ShipComponentsTable
                  recipe={recipe}
                  materials={materials}
                  components={recipe.components}
                  location={this.state.location}
                />
              )}
            </Toggle>
            ({Math.floor(divideMaterials(inventory, materials))}x available)
          </td>
          <td>
            <DropdownButton id="dropdown-basic-button" title={t('pages:recipes.actions.queue')} size={"sm"}>
              <Dropdown.Item onClick={() => this.queue(recipe, "main", this.context.userData.name)}>{t('pages:recipes.actions.queueForSelf')}</Dropdown.Item>
              {/*{this.context.userData.admin ?*/}
              {/*    <Dropdown.Item onClick={() => this.queue(recipe, "main", prompt("Enter pilot name"))}>Queue for other</Dropdown.Item> : null}*/}
              {this.context.roles.finance ?
                  <Dropdown.Item onClick={() => this.queue(recipe, "permanent", this.context.userData.name)}>{t('pages:recipes.actions.addToPermanentQueue')}</Dropdown.Item> : null}
            </DropdownButton>
            {/*{this.context.userData.admin ?*/}
            {/*  <button className="btn btn-primary btn-sm" onClick={() => {this.queue(recipe)}}>Queue</button>*/}
            {/*  : null}*/}
          </td>
        </tr>
      })}
      </tbody>
    </table>
  }

  render() {
    const { t } = this.props;
    return <div className={"container"}>
      <h3>{t('pages:recipes.orderShip')}</h3>
      <ul>
        <li>{t('pages:recipes.orderInstructions.commitment')}</li>
        <li>{t('pages:recipes.orderInstructions.fulfillment')}</li>
        <li>{t('pages:recipes.orderInstructions.location')}</li>
      </ul>

      <div className={"btn-toolbar mb-3"}>
        <div className={"input-group mr-2"}>
          <div className="input-group-prepend">
            <span className="input-group-text" id="">{t('common:labels.location')}</span>
          </div>
          <select className="form-control" value={this.state.location} onChange={e => this.setLocation(e)}>
            <option value="">{t('pages:recipes.allBases')}</option>
            {globalSettings.allowShipOrdersAt.map((id) =>
                <option key={id} value={id}>{globalSettings.locations[id]}</option>
            )}
          </select>
        </div>
        <div className={"input-group mr-2"}>
          <div className="input-group-prepend">
            <span className="input-group-text" id="">{t('common:labels.quantity')}</span>
          </div>
          <input className={"form-control"} type={"number"} value={this.state.quantity} onChange={e => this.setState({quantity: Math.max(1, e.target.value)})}/>
        </div>
      </div>


      <Card>
        <Card.Header>
          <ul className="nav nav-tabs card-header-tabs">
            {['doctrine'].concat(globalSettings.shipTypes).map(shipType => {
              if(shipType === "module" && !this.context.roles.engineer) {
                return null;
              }
              return <li className="nav-item" key={shipType}>
                <a className={"nav-link" + (this.state.shipType === shipType ? " active" : "")} href="#" onClick={evt => {
                  this.setState({
                    shipType: shipType,
                  });
                  evt.preventDefault()
                }}>{toTitleCase(shipType)}</a>
              </li>
            })}
          </ul>
        </Card.Header>


        {this.renderRecipesTable()}
      </Card>


      <h3>{t('pages:recipes.suggestShip')}</h3>
      <ul>
        <li>{t('pages:recipes.suggestInstructions.noCommitment')}</li>
        <li>{t('pages:recipes.suggestInstructions.pricing')}</li>
      </ul>
      <div className={"input-group mr-2"}>
        <select className="form-control" value={this.state.ship} onChange={e => this.setState({ship: e.target.value})}>
          <option value="">{t('pages:recipes.selectShip')}</option>
          {Object.keys(SHIP_DATA).sort().map(name =>
              <option key={name} value={name}>{name}</option>
          )}
        </select>
        <div className="input-group-append">
          <button className={"btn btn-primary"} onClick={e => this.suggest(e)}>{t('pages:recipes.buttons.suggest')}</button>
        </div>
      </div>
    </div>
  }
}
RecipesPage.contextType = GlobalInfo;

export default withTranslation(['pages', 'common'])(RecipesPage);